import React, { useState, useEffect } from "react";
import axios from "axios";
import DataTable from "react-data-table-component";
// import Header_Company from "../../Components/Header_Company";
import { BASE_URL } from "../../Config";
import Swal from "sweetalert2";

const Company_Supervisor = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [userId, setUserId] = useState(null);

  // Fetch userId from sessionStorage
  useEffect(() => {
    const storedUserData = sessionStorage.getItem("user");
    if (storedUserData) {
      const userData = JSON.parse(storedUserData);
      setUserId(userData.userId);
    } else {
      Swal.fire("Error", "No user data found in sessionStorage.", "error");
    }
  }, []);

  // Fetch agents after userId is available
  useEffect(() => {
    const fetchAgents = async () => {
      if (!userId) return;

      try {
        const response = await axios.get(
          `${BASE_URL}Company_Supervisor?userId=${userId}`,
          { headers: { accept: "*/*" } }
        );

        if (response.data.responseCode === "0000") {
          setAgents(response.data.agents);
        }
      } catch (error) {
        console.error("Error fetching agents:", error);
        Swal.fire("Error", "Failed to load agents.", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchAgents();
  }, [userId]);

  // Search & filter
  const filteredAgents = agents.filter((agent) => {
    const matchStatus = statusFilter === "All" || agent.status === statusFilter;
    const matchName = agent.name
      ?.toLowerCase()
      .includes(searchQuery.toLowerCase());
    return matchStatus && matchName;
  });

  // Status switch handler
  const handleStatusSwitch = async (agentId, currentStatus) => {
    const newStatus = currentStatus === "Active" ? "In-Active" : "Active";

    try {
      setAgents((prev) =>
        prev.map((agent) =>
          agent.agentId === agentId ? { ...agent, status: newStatus } : agent
        )
      );

      await axios.post(
        `${BASE_URL}Change_Status`,
        { agentId, status: newStatus },
        { headers: { "Content-Type": "application/json", accept: "*/*" } }
      );
    } catch (error) {
      console.error("Error changing status:", error);
      setAgents((prev) =>
        prev.map((agent) =>
          agent.agentId === agentId
            ? { ...agent, status: currentStatus }
            : agent
        )
      );
      Swal.fire("Error", "Failed to update status.", "error");
    }
  };

  // Inline editing handlers
  const handleEditField = (agentId, field) => {
    setAgents((prev) =>
      prev.map((agent) =>
        agent.agentId === agentId ? { ...agent, isEditing: true } : agent
      )
    );
  };

  const handleFieldChange = (agentId, field, value) => {
    setAgents((prev) =>
      prev.map((agent) =>
        agent.agentId === agentId ? { ...agent, [field]: value } : agent
      )
    );
  };

  const handleSaveField = async (agentId, field, value) => {
    try {
      await axios.post(
        `${BASE_URL}Edit_SupervisorDetails`,
        {
          userId: agentId,
          agentName: field === "name" ? value : undefined,
          cnic: field === "cnic" ? value : undefined,
          contactNumber: field === "contactNo" ? value : undefined,
        },
        { headers: { "Content-Type": "application/json", accept: "*/*" } }
      );

      setAgents((prev) =>
        prev.map((agent) =>
          agent.agentId === agentId ? { ...agent, isEditing: false } : agent
        )
      );
    } catch (error) {
      console.error("Error updating agent:", error);
      Swal.fire("Error", "Failed to save changes.", "error");
    }
  };

  // DataTable Columns
  const columns = [
    {
      name: "Agent Name",
      selector: (row) =>
        row.isEditing ? (
          <input
            value={row.name}
            onChange={(e) =>
              handleFieldChange(row.agentId, "name", e.target.value)
            }
            onBlur={() => handleSaveField(row.agentId, "name", row.name)}
            onKeyDown={(e) =>
              e.key === "Enter" &&
              handleSaveField(row.agentId, "name", row.name)
            }
          />
        ) : (
          <span onDoubleClick={() => handleEditField(row.agentId, "name")}>
            {row.name}
          </span>
        ),
      sortable: true,
    },
    {
      name: "CNIC",
      selector: (row) =>
        row.isEditing ? (
          <input
            value={row.cnic}
            onChange={(e) =>
              handleFieldChange(row.agentId, "cnic", e.target.value)
            }
            onBlur={() => handleSaveField(row.agentId, "cnic", row.cnic)}
            onKeyDown={(e) =>
              e.key === "Enter" &&
              handleSaveField(row.agentId, "cnic", row.cnic)
            }
          />
        ) : (
          <span onDoubleClick={() => handleEditField(row.agentId, "cnic")}>
            {row.cnic}
          </span>
        ),
      sortable: true,
    },
    {
      name: "Contact No",
      selector: (row) =>
        row.isEditing ? (
          <input
            value={row.contactNo}
            onChange={(e) =>
              handleFieldChange(row.agentId, "contactNo", e.target.value)
            }
            onBlur={() =>
              handleSaveField(row.agentId, "contactNo", row.contactNo)
            }
            onKeyDown={(e) =>
              e.key === "Enter" &&
              handleSaveField(row.agentId, "contactNo", row.contactNo)
            }
          />
        ) : (
          <span onDoubleClick={() => handleEditField(row.agentId, "contactNo")}>
            {row.contactNo}
          </span>
        ),
      sortable: true,
    },
    {
      name: "Status",
      selector: (row) => (
        <span
          className={`badge ${
            row.status === "Active" ? "bg-success" : "bg-secondary"
          }`}
        >
          {row.status}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <label className="switch">
          <input
            type="checkbox"
            checked={row.status === "Active"}
            onChange={() => handleStatusSwitch(row.agentId, row.status)}
          />
          <span className="slider round"></span>
        </label>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  return (
    <>
      {/* <Header_Company /> */}
      <div className="card m-5">
        <div className="card-header">
          <div className="row align-items-center">
            {/* Title Centered */}
            <div className="col-12 text-center mb-3">
              <h2 className="mb-0">Company Supervisor</h2>
            </div>

            {/* Filters + Search */}
            <div className="col-12 col-md-6 d-flex justify-content-center justify-content-md-start mb-3">
              <div className="d-flex flex-wrap gap-2">
                {["Active", "In-Active", "All"].map((status) => (
                  <button
                    key={status}
                    className={`btn btn-outline-${
                      status === "All"
                        ? "success"
                        : status === "Active"
                        ? "primary"
                        : "secondary"
                    } ${statusFilter === status ? "active" : ""}`}
                    onClick={() => setStatusFilter(status)}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>

            <div className="col-12 col-md-6 mb-2 mb-md-0">
              <input
                type="text"
                className="form-control"
                placeholder="Search by Supervisor name"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="card-body">
          {/* Data Table */}
          {loading ? (
            <div className="text-center">Loading agents...</div>
          ) : (
            <DataTable
              columns={columns}
              data={filteredAgents}
              pagination
              highlightOnHover
              responsive
              striped
              noDataComponent="No agents found."
            />
          )}
        </div>
      </div>
    </>
  );
};

export default Company_Supervisor;
